/* eslint-disable @atlassian/relay/unused-fields */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import {
	Fragment,
	useRef,
	type ReactNode,
	Suspense,
	useContext,
	useEffect,
	useMemo,
	useState,
	forwardRef,
	type ForwardRefExoticComponent,
	type RefAttributes,
} from 'react';

import { jsx } from '@compiled/react';
import stringify from 'fast-json-stable-stringify';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { CURRENT_SURFACE_CSS_VAR } from '@atlaskit/tokens/constants';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import { useForgeUiAnalyticsEvent } from '../../analytics/useForgeUiAnalyticsEvent';
import { EnvironmentContext } from '../../context';
import { TracingProvider } from '../../error-reporting';
import { parseExtensionId, useMemoizedStateObject } from '../../utils';
import { getExtensionType } from '../../utils/getExtensionType';
import { ContextChangeObserverWrapper } from './ContextChangeObserverWrapper';
import { InnerIframe } from './InnerIframe';
import { TunnelsProvider } from './TunnelsProvider';
import type { HTMLCustomIFrameElement, IframeProps, ViewContext } from './types';
import {
	createSrcFromExtension,
	getBrowserLocale,
	getBrowserTimezone,
	getResolverContext,
	getSrcUrl,
	getViewContext,
	shouldUseCDNAuth,
	useCDNAuthSignedCookie,
} from './utils';
import { urlSafeB64 } from './utils/createSrcFromExtension';
import { getCoreDataInner } from './utils/getCoreDataInner';
import { getEncodedAppContext } from './utils/getEncodedAppContext';
import { getExtensionEntryPoint } from './utils/getExtensionEntryPoint';
import { getLoadingComponent } from './utils/getLoadingComponent';
import { InnerIframe as InnerIframeV2 } from './v2/InnerIframe';

const styles = cssMap({
	hostedResourcesIframeContainer: {
		position: 'relative',
	},
	box: {
		width: '100%',
		height: '100%',
	},
});

const ReloadOnContextChangeWrapper = ({
	context,
	children,
	getCurrentForgeReactMajorVersion,
}: {
	children: ReactNode;
	context: ViewContext;
	getCurrentForgeReactMajorVersion: IframeProps['getCurrentForgeReactMajorVersion'];
}) => {
	if (getCurrentForgeReactMajorVersion) {
		return (
			<ContextChangeObserverWrapper
				context={context}
				getCurrentForgeReactMajorVersion={getCurrentForgeReactMajorVersion}
			>
				{children}
			</ContextChangeObserverWrapper>
		);
	}
	return <Fragment key={stringify(context)}>{children}</Fragment>;
};

export const Iframe: ForwardRefExoticComponent<
	IframeProps & RefAttributes<HTMLCustomIFrameElement>
> = forwardRef<HTMLCustomIFrameElement, IframeProps>(
	(
		{
			accountId,
			extension,
			contextIds,
			components,
			coreData,
			extensionData,
			extensionViewData,
			loadingComponent,
			onLoad,
			height = '100%',
			initialIframeHeight,
			width = '100%',
			bridge,
			customBridgeMethods,
			customBridge,
			customBridgeContext,
			isResizable = true,
			entryPoint,
			extensionPayload,
			getContextToken,
			timezone,
			locale,
			isHidden = false,
			isInModal = false,
			isInlinePresentation = false,
			setShouldShowRenderer,
			setIsLoading,
			getCurrentForgeReactMajorVersion,
			onError,
			isEmbeddedMacro = false,
			embeddedMacroParentType,
			embeddedParentLocalId,
			contextChangeListeners,
		},
		iframeRef,
	) => {
		const environment = useContext(EnvironmentContext);

		// ARKEN-856: useThemeObserver() is a bit noisy, it returns copies of the same theme which breaks useBridge() dependencies
		// We use useMemoizedStateObject() to properly memoize its output doing a deep-equality check
		const theme = useMemoizedStateObject(useThemeObserver());
		const iframeWrapperRef = useRef<HTMLDivElement>(null);

		const [surfaceColor, setSurfaceColor] = useState<string | null>(null);
		const { trackExtensionLoaded } = useForgeUiAnalyticsEvent();

		useEffect(() => {
			if (!iframeWrapperRef.current) {
				return;
			}
			const nextSurfaceColor = getComputedStyle(iframeWrapperRef.current).getPropertyValue(
				CURRENT_SURFACE_CSS_VAR,
			);

			setSurfaceColor(nextSurfaceColor);
		}, [theme]);

		const { appId, moduleKey } = parseExtensionId(extension.id);
		const { environmentId } = extension;

		// getExtensionEntryPoint resolves both the resource and its resolvedResourceEntry from the
		// active entryPoint together, so the top-level resource's HTML filename does not bleed into a
		// sub-entry-point's (config/edit/view/create/contextConfig) CDN URL.
		const { resource: extensionEntryPoint, resolvedResourceEntry: activeResolvedResourceEntry } =
			getExtensionEntryPoint(extension, entryPoint);

		const extensionViewDataMemoKey = stringify(extensionViewData);

		// eslint-disable-next-line react-hooks/exhaustive-deps -- extensionViewDataMemoKey is a deep-equality proxy; extensionViewData is intentionally excluded to avoid referential-inequality re-renders.
		const stableExtensionViewData = useMemo(() => extensionViewData, [extensionViewDataMemoKey]);

		const urlSafeContext = useMemo(
			() => urlSafeB64(getEncodedAppContext(extension, stableExtensionViewData, environment)),
			[extension, stableExtensionViewData, environment],
		);

		const defaultSrc = useMemo(
			() =>
				createSrcFromExtension(
					appId,
					extension,
					environment,
					extensionEntryPoint,
					stableExtensionViewData,
					activeResolvedResourceEntry,
				),
			[
				appId,
				extension,
				environment,
				extensionEntryPoint,
				stableExtensionViewData,
				activeResolvedResourceEntry,
			],
		);

		const isSignedCookieLoading = useCDNAuthSignedCookie();

		useEffect(() => {
			const renderType = extension?.properties?.render === 'native' ? 'UIKit' : 'CustomUI';
			const extensionType = getExtensionType(extension);
			trackExtensionLoaded({
				forgeEnvironment: extension?.environmentType,
				renderType,
				extensionType,
			});
			// This should run for each new unique appId/moduleKey combination
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [appId, moduleKey]);

		const [isThreeLOPromptVisible, setIsThreeLOPromptVisible] = useState(false);

		const coreDataInner = getCoreDataInner(coreData, extension);

		const viewportSize = extension.properties.viewportSize;
		// Manifest viewportSize normally disables iframe auto-resize (fixed shell heights).
		// viewportSize: 'resizable' is paired with resizable modal chrome — keep iframe resizer on.
		const isResizableViewportSize = viewportSize === 'resizable';
		const resizingEnabled =
			isResizable && (isInlinePresentation || !viewportSize || isResizableViewportSize);

		// moves specific macro extension data (isEditing, macro) to extensionData instead of extensionPayload as this is required for Tempo (#hot-300905)
		// the first check enables the feature for Tempo specifically, the second check enables the feature for all extensions if needed
		const enabledForTempo =
			appId === '76206a70-b9d6-4328-9e20-db79196c548a' && fg('forge-ui-move-macro-data-for-tempo');
		const shouldIncludeMacroDataInExtensionData =
			enabledForTempo || fg('forge-ui-move-macro-data-to-extension-data');

		return (
			<TunnelsProvider
				environmentType={extension.environmentType}
				appId={appId}
				contextIds={contextIds}
				environmentId={environmentId}
			>
				{({ tunnels, loading }) => (
					<Box
						ref={iframeWrapperRef}
						testId="hosted-resources-iframe-container"
						xcss={styles.hostedResourcesIframeContainer}
						style={{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
							minHeight:
								isThreeLOPromptVisible ||
								isHidden ||
								(fg('platform_forge_ui_refactor_macro_parent_type')
									? !!embeddedMacroParentType
									: isEmbeddedMacro)
									? undefined
									: height,
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
							height: resizingEnabled || isThreeLOPromptVisible || isHidden ? undefined : height,
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
							width,
						}}
					>
						{(loading || (shouldUseCDNAuth() && isSignedCookieLoading)) && (
							<Suspense fallback={null}>
								<Box xcss={styles.box}>
									{/* This hides another loading spinner we have that is shown while we are fetching the list of current active tunnels.
									This is not needed for UI Kit 2 (where isHidden = true) since we'll already show a loading spinner with the RendererNext component */}
									{loading && !isHidden && getLoadingComponent(loadingComponent)}
								</Box>
							</Suspense>
						)}
						{!(loading || (shouldUseCDNAuth() && isSignedCookieLoading)) && (
							<TracingProvider>
								{fg('forge-ui-bridge-core-reset') ? (
									<InnerIframeV2
										accountId={accountId}
										extension={extension}
										contextIds={contextIds}
										components={components}
										coreData={coreDataInner}
										customBridge={customBridge}
										customBridgeContext={customBridgeContext}
										extensionData={extensionData}
										extensionViewData={extensionViewData}
										loadingComponent={loadingComponent}
										onLoad={onLoad}
										height={height}
										initialIframeHeight={initialIframeHeight}
										width={width}
										src={getSrcUrl(
											tunnels,
											extensionEntryPoint,
											defaultSrc,
											urlSafeContext,
											activeResolvedResourceEntry,
										)}
										bridge={bridge}
										customBridgeMethods={customBridgeMethods}
										isResizable={resizingEnabled}
										getContextToken={getContextToken}
										timezone={timezone}
										locale={locale}
										setIsThreeLOPromptVisible={setIsThreeLOPromptVisible}
										setShouldShowRenderer={setShouldShowRenderer}
										extensionPayload={extensionPayload}
										isHidden={isHidden}
										theme={theme}
										surfaceColor={surfaceColor}
										appId={appId}
										ref={iframeRef}
										isInModal={isInModal}
										setIsLoading={setIsLoading}
										onError={onError}
										isEmbeddedMacro={isEmbeddedMacro}
										embeddedMacroParentType={embeddedMacroParentType}
										embeddedParentLocalId={embeddedParentLocalId}
										contextChangeListeners={contextChangeListeners}
									/>
								) : (
									<ReloadOnContextChangeWrapper
										getCurrentForgeReactMajorVersion={getCurrentForgeReactMajorVersion}
										context={getViewContext({
											resolverContext: getResolverContext(coreDataInner, extensionData),
											accountId,
											extension,
											timezone: timezone ?? getBrowserTimezone(),
											locale: locale ?? getBrowserLocale(),
											extensionViewData,
											extensionPayload: {
												config: extensionPayload?.config,
												macro: shouldIncludeMacroDataInExtensionData
													? extensionData?.macro
													: extensionPayload?.macro,
												isEditing: shouldIncludeMacroDataInExtensionData
													? extensionData?.isEditing
													: extensionPayload?.isEditing,
											},
										})}
									>
										<InnerIframe
											accountId={accountId}
											extension={extension}
											contextIds={contextIds}
											components={components}
											coreData={coreDataInner}
											extensionData={extensionData}
											extensionViewData={extensionViewData}
											loadingComponent={loadingComponent}
											onLoad={onLoad}
											height={height}
											initialIframeHeight={initialIframeHeight}
											width={width}
											src={getSrcUrl(
												tunnels,
												extensionEntryPoint,
												defaultSrc,
												urlSafeContext,
												activeResolvedResourceEntry,
											)}
											bridge={bridge}
											customBridgeMethods={customBridgeMethods}
											isResizable={resizingEnabled}
											getContextToken={getContextToken}
											timezone={timezone}
											locale={locale}
											setIsThreeLOPromptVisible={setIsThreeLOPromptVisible}
											setShouldShowRenderer={setShouldShowRenderer}
											extensionPayload={extensionPayload}
											isHidden={isHidden}
											theme={theme}
											surfaceColor={surfaceColor}
											appId={appId}
											ref={iframeRef}
											isInModal={isInModal}
											setIsLoading={setIsLoading}
											onError={onError}
											isEmbeddedMacro={isEmbeddedMacro}
											embeddedMacroParentType={embeddedMacroParentType}
											embeddedParentLocalId={embeddedParentLocalId}
										/>
									</ReloadOnContextChangeWrapper>
								)}
							</TracingProvider>
						)}
					</Box>
				)}
			</TunnelsProvider>
		);
	},
);
